import React from 'react';
import {Button, Popconfirm, Tooltip} from 'antd';
import {LineChartOutlined, PlusCircleOutlined, PoweroffOutlined, RedoOutlined,} from '@ant-design/icons';
import type {HedgePosition} from '../../../types/hedge';
import {HedgeProtectRows} from './HedgeProtectRows';
import {
    BIAS_META,
    CLOSE_REASON_TEXT,
    fmtCompactTime,
    fmtPct,
    fmtPnl,
    fmtPx,
    fmtQty,
    LEG_EXIT_REASON_TEXT
} from './hedgeDisplay';

/** 完成度三态徽标（主单+对冲单均完成成交才算完成） */
const COMPLETION_META: Record<string, { text: string; cls: string }> = {
    COMPLETE: {text: '✓ 已完成对冲', cls: 'complete'},
    PARTIAL: {text: '◐ 部分对冲', cls: 'partial'},
    INCOMPLETE: {text: '⚠ 未完成对冲', cls: 'incomplete'},
    SINGLE_LEG: {text: '◑ 单腿持有', cls: 'partial'},
    CLOSED: {text: '已平仓', cls: 'closed'},
    COMPENSATED: {text: '已补偿退出', cls: 'compensated'},
};

/** 单腿盈亏（USDT，盈利带 + 号，不带百分比） */
const fmtLegPnl = (pnl?: number): string =>
    null == pnl ? '--' : `${Number(pnl) >= 0 ? '+' : ''}${Number(pnl).toFixed(2)}U`;

/** 腿完成度标记（✓ 全额 / ◐ 部分 / ✗ 未成交） */
const legMark = (pct?: number): string => {
    const value = Number(pct ?? 0);
    return value >= 99.9 ? '✓' : (value > 0 ? '◐' : '✗');
};

/** 保护单状态文案: 0=未挂, 1=已挂, 2=部分, 3=失败待补挂 */
const protectText = (status?: number): string => {
    switch (Number(status)) {
        case 1:
            return '已挂载';
        case 2:
            return '部分挂载';
        case 3:
            return '挂载失败（待补挂）';
        default:
            return '未挂载';
    }
};

interface Props {
    position: HedgePosition;
    busy?: boolean;
    /** 是否当前激活（点击卡片后下方执行记录联动该品种） */
    active?: boolean;
    onActivate?: (position: HedgePosition) => void;
    onClose: (id: number) => void;
    onRebalance: (id: number) => void;
    onRetry: (id: number) => void;
    onOpenChart: (position: HedgePosition, market: 'SPOT' | 'SWAP') => void;
}

/** 单腿展示视图（主单/对冲单按角色固定左右顺序，不随现货/永续顺序漂移） */
interface LegView {
    market: 'SPOT' | 'SWAP';
    title: string;
    instId?: string;
    qty?: number;
    qtyUnit: string;
    avgPx?: number;
    filledQty?: number;
    filledPct?: number;
    lastPx?: number;
    pnl?: number;
    /** 退出原因（单腿独立离场时后端写入；止盈/止损/保护单触发） */
    exitReason?: string;
}

/**
 * 对冲持仓卡片（紧凑双腿对照 + 完成度三态 + 图标化手动干预）
 * 腿部行合并为「均价→现价 / 成交 / 盈亏」3 行，其他次级信息走 Tooltip，压缩卡片高度。
 */
export const HedgePositionCard: React.FC<Props> = ({
                                                       position,
                                                       busy,
                                                       active,
                                                       onActivate,
                                                       onClose,
                                                       onRebalance,
                                                       onRetry,
                                                       onOpenChart,
                                                   }) => {
    const p = position;
    const completion = COMPLETION_META[p.completionState] ?? COMPLETION_META.INCOMPLETE;
    const bias = BIAS_META[p.bias ?? ''] ?? {text: p.bias ?? '--', cls: ''};
    const spotIsMain = 'SPOT' === p.mainLeg;
    const spotPct = spotIsMain ? p.mainFilledPct : p.hedgeFilledPct;
    const swapPct = spotIsMain ? p.hedgeFilledPct : p.mainFilledPct;
    const running = 'CLOSED' !== p.completionState && 'COMPENSATED' !== p.completionState;
    const abnormal = 'INCOMPLETE' === p.completionState || 'NEEDS_MANUAL' === p.status
        || 'FAILED' === p.status || null != p.lastError;
    const timeInfo = fmtCompactTime(p.createdAt);

    const spotLeg: LegView = {
        market: 'SPOT',
        title: spotIsMain ? '主单 · 现货买入' : '对冲单 · 现货买入',
        instId: p.spotInstId,
        qty: p.spotQty,
        qtyUnit: p.symbol,
        avgPx: p.spotAvgPx,
        filledQty: p.spotFilledQty,
        filledPct: spotPct,
        lastPx: p.spotLastPx,
        pnl: p.spotUnrealizedPnlUsdt,
        exitReason: p.spotExitReason,
    };
    const swapLeg: LegView = {
        market: 'SWAP',
        title: spotIsMain ? '对冲单 · 永续空' : '主单 · 永续空',
        instId: p.swapInstId,
        qty: p.swapQty,
        // 成交/目标量均为币量口径（后端 toCoinQty 已按 ctVal 归一），与现货腿同单位
        qtyUnit: p.symbol,
        avgPx: p.swapAvgPx,
        filledQty: p.swapFilledQty,
        filledPct: swapPct,
        lastPx: p.swapLastPx,
        pnl: p.swapUnrealizedPnlUsdt,
        exitReason: p.swapExitReason,
    };
    // 主单固定左侧、对冲单固定右侧
    const legs = spotIsMain ? [spotLeg, swapLeg] : [swapLeg, spotLeg];

    /** 现价相对均价的涨跌幅（%）；无数据返回 undefined */
    const legDeltaPct = (leg: LegView): number | undefined => {
        if (null == leg.lastPx || null == leg.avgPx || 0 === Number(leg.avgPx)) {
            return undefined;
        }
        return ((Number(leg.lastPx) - Number(leg.avgPx)) / Number(leg.avgPx)) * 100;
    };
    /** 该腿价格变动是否有利：现货多头涨有利 / 永续空头跌有利 */
    const isFavorable = (leg: LegView, delta: number): boolean =>
        'SPOT' === leg.market ? delta >= 0 : delta <= 0;

    return (
        <div className={`hedge-card ${completion.cls}${active ? ' active' : ''}`}
             onClick={() => onActivate?.(p)}>
            <div className="hedge-card-head">
                <span className="hedge-card-symbol">{p.symbol}</span>
                <span className={`hedge-bias ${bias.cls}`}>{bias.text}</span>
                <span className={`hedge-completion ${completion.cls}`}>{completion.text}</span>
                <div className="hedge-card-ops" onClick={(e) => e.stopPropagation()}>
                    {running && 'PARTIAL' === p.completionState && (
                        <Tooltip title="补单">
                            <Button size="small" type="text" className="hedge-icon-btn" aria-label="补单"
                                    icon={<PlusCircleOutlined/>} loading={busy} onClick={() => onRebalance(p.id)}/>
                        </Tooltip>
                    )}
                    {running && ('INCOMPLETE' === p.completionState || 'NEEDS_MANUAL' === p.status) && (
                        <Tooltip title="重试">
                            <Button size="small" type="text" className="hedge-icon-btn" aria-label="重试"
                                    icon={<RedoOutlined/>} loading={busy} onClick={() => onRetry(p.id)}/>
                        </Tooltip>
                    )}
                    {running && (
                        <Popconfirm title="确认市价平掉双腿？" onConfirm={() => onClose(p.id)}
                                    okText="平仓" cancelText="取消">
                            <Button size="small" type="text" danger className="hedge-icon-btn" aria-label="平仓"
                                    title="市价平仓双腿" icon={<PoweroffOutlined/>} loading={busy}/>
                        </Popconfirm>
                    )}
                </div>
                <span className="hedge-card-time" title={timeInfo.full}>{timeInfo.text}</span>
            </div>

            <div className="hedge-legs">
                {legs.map(leg => {
                    const delta = legDeltaPct(leg);
                    const fullFill = Number(leg.filledPct ?? 0) >= 99.9;
                    return (
                        <div className="hedge-leg" key={leg.market}>
                            <div className="hedge-leg-title">
                                <span>{leg.title}</span>
                                <span className="hedge-leg-mark">{legMark(leg.filledPct)}</span>
                                <span className="hedge-leg-inst" title={leg.instId}>
                                    {'SPOT' === leg.market ? 'SPOT' : 'SWAP'}
                                </span>
                                <Tooltip title={`${'SPOT' === leg.market ? '现货' : '永续'}K线 ${leg.instId ?? ''}`}>
                                    <Button size="small" type="text" className="hedge-kline-btn" aria-label="K线"
                                            icon={<LineChartOutlined/>}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onOpenChart(p, leg.market);
                                            }}/>
                                </Tooltip>
                            </div>
                            <div className="hedge-leg-row">
                                <span className="hedge-leg-label">均价</span>
                                <span className="hedge-leg-value">{fmtPx(leg.avgPx)}</span>
                            </div>
                            <div className="hedge-leg-row">
                                <span className="hedge-leg-label">现价</span>
                                <span className="hedge-leg-value">
                                    {fmtPx(leg.lastPx)}
                                    {null != delta && (
                                        <span className={`hedge-leg-delta ${isFavorable(leg, delta) ? 'ok' : 'warn'}`}>
                                            {delta >= 0 ? ' +' : ' '}{delta.toFixed(2)}%
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="hedge-leg-row">
                                <span className="hedge-leg-label">成交</span>
                                <span className="hedge-leg-value">
                                    {fmtQty(leg.filledQty)}
                                    {fullFill ? '' : `/${fmtQty(leg.qty)}`} {leg.qtyUnit}
                                    <span className={fullFill ? ' dim' : ' warn'}> （{fmtPct(leg.filledPct)}）</span>
                                </span>
                            </div>
                            <div className="hedge-leg-row">
                                <span className="hedge-leg-label">盈亏</span>
                                <span className={`hedge-leg-value${null == leg.pnl ? ' dim'
                                    : (Number(leg.pnl) >= 0 ? ' ok' : ' warn')}`}>
                                    {fmtLegPnl(leg.pnl)}
                                </span>
                            </div>
                            {null != leg.exitReason && (
                                <div className="hedge-leg-row">
                                    <span className="hedge-leg-label">离场</span>
                                    <span className="hedge-leg-value warn">
                                        {LEG_EXIT_REASON_TEXT[leg.exitReason] ?? leg.exitReason}
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {running && <HedgeProtectRows position={p}/>}

            <div className="hedge-card-foot">
                <span title="目标对冲比例">对冲率 {fmtPct(p.hedgeRatio)}</span>
                <span className={Number(p.deviationPct ?? 0) > 10 ? 'warn' : 'ok'}
                      title="实际对冲比例相对目标的偏离（>10% 触发补单）">
                    偏离 {fmtPct(p.deviationPct)}
                </span>
                <span className={Number(p.unhedgedQty ?? 0) > 0 ? 'warn' : 'ok'} title="未对冲敞口数量">
                    敞口 {fmtQty(p.unhedgedQty)}
                </span>
                <span title="总投入">投入 {fmtQty(p.totalUsdt)}U</span>
                {null != p.unrealizedPnlUsdt && (
                    <span className={Number(p.unrealizedPnlUsdt) >= 0 ? 'ok' : 'warn'} title="双腿未实现盈亏合计">
                        合计 {fmtPnl(p.unrealizedPnlUsdt, p.unrealizedPnlPct)}
                    </span>
                )}
                <span className={1 === Number(p.protectStatus) ? 'ok' : 'warn'} title="交易所保护单状态">
                    保护 {protectText(p.protectStatus)}
                </span>
                {Number(p.reprotectCount ?? 0) > 0 && (
                    <span className="ok"
                          title={`单腿期连续2次同向研报触发的保护腿重挂（第 ${p.reprotectCount} 次成功`
                              + `${null == p.reprotectAt ? '' : '，最近 ' + fmtCompactTime(p.reprotectAt).full}）`
                              + '，新腿保护价已按触发研报刷新'}>
                        重挂 ×{p.reprotectCount}
                    </span>
                )}
                {1 === Number(p.reprotectFrozen) && (
                    <span className="warn"
                          title="重挂的新保护腿因止损离场；下一次连续2次同向研报触发将跳过一轮重挂，需重新连续2次">
                        重挂已冻结
                    </span>
                )}
            </div>

            {null != p.closeReason && (
                <div className="hedge-card-note">
                    平仓原因：{CLOSE_REASON_TEXT[p.closeReason] ?? p.closeReason}
                </div>
            )}
            {null != p.protectError && (
                /已被现价越过/.test(p.protectError)
                    ? <div className="hedge-card-note warn" title={p.protectError}>保护单：{p.protectError}</div>
                    : <div className="hedge-card-error" title={p.protectError}>保护单：{p.protectError}</div>
            )}
            {abnormal && null != p.lastError && (
                <div className="hedge-card-error" title={p.lastError}>{p.lastError}</div>
            )}
        </div>
    );
};
